
import React, { useState, useEffect, useRef } from 'react';
import './Header.css';
import { useApp } from '../../context/AppContext';

interface HeaderProps {
  isAdminView: boolean;
  setIsAdminView: (value: boolean) => void;
  unreadNotifications: number;
}

const Header: React.FC<HeaderProps> = ({ isAdminView, setIsAdminView, unreadNotifications }) => {
  const { activeTab, selectedProduct, setSelectedProduct, adminUser, notifications, markNotificationRead } = useApp();
  const [showNotifications, setShowNotifications] = useState(false);
  const notificationRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getTitle = () => {
    if (selectedProduct) return 'Product Details';
    switch (activeTab) {
      case 'home': return adminUser.displayName;
      case 'shop': return 'Shop';
      case 'cart': return 'My Cart';
      case 'messages': return 'Messages';
      case 'profile': return isAdminView ? 'Creator Dashboard' : 'My Profile';
      default: return 'OnlyJalen';
    }
  };

  const showBackButton = selectedProduct !== null;

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'post': return '📸';
      case 'message': return '💬';
      case 'order': return '📦';
      case 'product': return '🛍️';
      case 'tip': return '💰';
      case 'welcome': return '🎉';
      default: return '🔔';
    }
  };

  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const handleNotificationClick = (notificationId: string) => {
    markNotificationRead(notificationId);
  };

  const handleMarkAllRead = () => {
    notifications.forEach(n => {
      if (!n.read) markNotificationRead(n.id);
    });
  };

  return (
    <header className="header light">
      <div className="header-left">
        {showBackButton ? (
          <button className="header-btn back-btn" onClick={() => setSelectedProduct(null)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M15 18l-6-6 6-6"/>
            </svg>
          </button>
        ) : (
          <div className="header-logo">
            <div className="logo-icon">
              <img src="https://jalenward.com/images/blackwhitetextjalen.png" alt="OnlyJalen" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            </div>
          </div>
        )}
      </div>

      <div className="header-center">
        <h1 className="header-title">{getTitle()}</h1>
        {activeTab === 'home' && !showBackButton && (
          <span className="header-subtitle">Exclusive Content & Fashion</span>
        )}
      </div>

      <div className="header-right">
        {activeTab === 'profile' && (
          <button
            className={`admin-toggle ${isAdminView ? 'active' : ''}`}
            onClick={() => setIsAdminView(!isAdminView)}
            title={isAdminView ? 'Switch to Fan View' : 'Switch to Creator View'}
          >
            {isAdminView ? '👤' : '⭐'}
          </button>
        )}
        <div className="notification-container" ref={notificationRef}>
          <button className="header-btn notification-btn" onClick={() => setShowNotifications(!showNotifications)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/>
              <path d="M13.73 21a2 2 0 01-3.46 0"/>
            </svg>
            {unreadNotifications > 0 && (
              <span className="notification-badge">{unreadNotifications > 9 ? '9+' : unreadNotifications}</span>
            )}
          </button>

          {showNotifications && (
            <div className="notification-dropdown">
              <div className="notification-header">
                <h3>Notifications</h3>
                {unreadNotifications > 0 && (
                  <button className="mark-all-read" onClick={handleMarkAllRead}>
                    Mark all read
                  </button>
                )}
              </div>
              <div className="notification-list">
                {notifications.length === 0 ? (
                  <div className="notification-empty">
                    <span>🔔</span>
                    <p>No notifications yet</p>
                  </div>
                ) : (
                  notifications.slice(0, 10).map(notification => (
                    <div
                      key={notification.id}
                      className={`notification-item ${notification.read ? '' : 'unread'}`}
                      onClick={() => handleNotificationClick(notification.id)}
                    >
                      <div className="notification-icon">
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div className="notification-content">
                        <div className="notification-title">{notification.title}</div>
                        <div className="notification-message">{notification.message}</div>
                        <div className="notification-time">{formatTimeAgo(new Date(notification.timestamp))}</div>
                      </div>
                      {!notification.read && <div className="notification-dot" />}
                    </div>
                  ))
                )}
              </div>
              {notifications.length > 0 && (
                <div className="notification-footer">
                  <button onClick={() => setShowNotifications(false)}>View All</button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
